import { SelectHTMLAttributes, forwardRef, ReactNode } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  /** Mensaje de error */
  error?: string;
  /** Label del select */
  label?: string;
  /** Texto de ayuda */
  helpText?: string;
  /** Opciones del select */
  options: SelectOption[];
  /** Placeholder (primera opción deshabilitada) */
  placeholder?: string;
  /** Icono a mostrar a la izquierda del select */
  leadingIcon?: ReactNode;
}

/**
 * Select - Select nativo con estilos Tailwind Plus
 * 
 * @example
 * ```tsx
 * // Básico
 * <Select
 *   label="Location"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' },
 *     { value: 'mx', label: 'Mexico' },
 *   ]}
 *   placeholder="Select a country"
 * />
 * 
 * // Con leading icon
 * <Select
 *   label="Gender"
 *   leadingIcon={<UserIcon />}
 *   options={[...]}
 * />
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ 
    error, 
    label, 
    helpText, 
    options, 
    placeholder,
    leadingIcon,
    className = '', 
    id, 
    disabled,
    ...props 
  }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substring(2, 9)}`;
    const hasError = !!error;
    const hasLeadingIcon = !!leadingIcon;
    
    const describedBy = hasError 
      ? `${selectId}-error` 
      : helpText 
        ? `${selectId}-description` 
        : undefined;

    return (
      <div>
        {label && (
          <label 
            htmlFor={selectId} 
            className="block text-sm/6 font-medium text-secondary-900 dark:text-white"
          >
            {label}
          </label>
        )}
        <div className={cn(label && 'mt-2', 'grid grid-cols-1')}>
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={cn(
              // Grid positioning
              'col-start-1 row-start-1',
              // Base
              'w-full appearance-none rounded-md bg-white py-1.5 pr-8 text-base sm:text-sm/6',
              // Padding ajustado para iconos
              hasLeadingIcon ? 'pl-10 sm:pl-9' : 'pl-3',
              // Normal state - Light mode
              !hasError && [
                'text-secondary-900 border border-secondary-300',
                'focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
              ],
              // Error state - Light mode
              hasError && [
                'text-danger-900 border border-danger-300',
                'focus:border-danger-500 focus:ring-1 focus:ring-danger-500',
              ],
              // Normal state - Dark mode
              !hasError && [
                'dark:bg-white/5 dark:text-white dark:border-white/10',
                'dark:focus:border-primary-500 dark:focus:ring-primary-500',
              ],
              // Error state - Dark mode
              hasError && [
                'dark:bg-danger-950/20 dark:text-danger-300 dark:border-danger-500/50',
                'dark:focus:border-danger-500 dark:focus:ring-danger-500',
              ],
              // Disabled state
              'disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-500 disabled:border-secondary-200',
              'dark:disabled:bg-white/[0.02] dark:disabled:text-secondary-500 dark:disabled:border-white/5',
              // Focus outline
              'focus:outline-none',
              className
            )}
            aria-invalid={hasError ? 'true' : undefined}
            aria-describedby={describedBy}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option 
                key={option.value} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Leading icon */}
          {hasLeadingIcon && (
            <div 
              className={cn(
                'pointer-events-none col-start-1 row-start-1 ml-3 flex items-center self-center',
                hasError 
                  ? 'text-danger-500 dark:text-danger-400' 
                  : 'text-secondary-400 dark:text-secondary-500'
              )}
              aria-hidden="true"
            >
              <span className="size-5 sm:size-4">
                {leadingIcon}
              </span>
            </div>
          )}
          
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(
              'pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end sm:size-4',
              hasError 
                ? 'text-danger-500 dark:text-danger-400' 
                : 'text-secondary-500 dark:text-secondary-400'
            )}
          />
        </div>
        
        {/* Help text */}
        {helpText && !hasError && (
          <p 
            id={`${selectId}-description`} 
            className="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
          >
            {helpText}
          </p>
        )}
        
        {/* Error message */}
        {hasError && (
          <p 
            id={`${selectId}-error`} 
            className="mt-2 text-sm text-danger-600 dark:text-danger-400" 
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
